import React, { useState, memo } from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import Spinner from '@atlaskit/spinner/spinner';

import type { ImagePickerComponentProps } from '../pageCoverPictureTypes.ts';

import { AddHeaderImageButton } from './AddHeaderImageButton.tsx';
import { ImageRepositioner } from './ImageRepositioner.tsx';
import { HoverOverlayAndDropdownItems } from './HoverOverlayAndDropdownItems.tsx';
import { AIUnsplashHeaderToolbar } from './AIUnsplashHeaderToolbar.tsx';

type UploadingPictureWrapperProps = {
	hasCoverPicture: boolean;
	dynamicCoverImageHeight?: number;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UploadingPictureWrapper = styled.div<UploadingPictureWrapperProps>({
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: (props) => `${props.dynamicCoverImageHeight}px`,
	position: 'relative',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	background: token('color.background.accent.gray.subtlest'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
});

export const ImagePicker = memo(
	({
		hasCoverPicture,
		openMediaBrowser,
		onRemoveImage,
		userOffline,
		setIsUploadingPicture,
		isUploadingPicture,
		handleUnsplashUpload,
		initialPicturePosition,
		onSaveReposition,
		shouldShowReposition,
		setShouldShowReposition,
		coverPictureState,
		floatingToolbarButtonStyle,
		forceUpdate,
		contentId,
		dynamicCoverImageHeight,
		onImageWidthChange,
		spaceKey,
		onRegenerate,
		isRegeneratingImage,
		onAcceptAIUnsplashImage,
		shouldShowAIUnsplashHeaderToolbar,
		isFixedWidthImageOption,
		generateAIHeaderImage,
	}: ImagePickerComponentProps) => {
		const [isModalOpenState, setIsModalOpenState] = useState<boolean>(false);
		const [isRepositionActive, setIsRepositionActive] = useState<boolean>(false);
		const [initialUnsplashInfoArray, setInitialUnsplashInfoArray] = useState<Array<object>>([]);

		const openModal = () => {
			setIsModalOpenState(true);
			void coverPictureState.setIsModalOpen(true);
		};
		const closeModal = () => {
			setIsModalOpenState(false);
			void coverPictureState.setIsModalOpen(false);
		};

		if (isUploadingPicture && coverPictureState.state.isUploadingPicture) {
			return (
				<UploadingPictureWrapper
					data-testid="uploading-picture-wrapper"
					hasCoverPicture={hasCoverPicture}
					dynamicCoverImageHeight={dynamicCoverImageHeight}
				>
					<Spinner size="medium" />
				</UploadingPictureWrapper>
			);
		} else if (
			(shouldShowAIUnsplashHeaderToolbar ||
				(coverPictureState.state.shouldShowAIUnsplashHeaderToolbar && !shouldShowReposition)) &&
			hasCoverPicture
		) {
			return (
				<AIUnsplashHeaderToolbar
					onAccept={onAcceptAIUnsplashImage}
					onRegenerate={onRegenerate}
					onRemoveImage={onRemoveImage}
					isRegeneratingImage={isRegeneratingImage}
				/>
			);
		} else if (
			(isRepositionActive ||
				shouldShowReposition ||
				!!coverPictureState.state.shouldShowReposition) &&
			hasCoverPicture
		) {
			// shouldShowReposition = true when we just uploaded a photo
			return (
				<ImageRepositioner
					initialPicturePosition={initialPicturePosition}
					onSaveReposition={onSaveReposition}
					setIsRepositionActive={setIsRepositionActive}
					setShouldShowReposition={setShouldShowReposition}
					coverPictureState={coverPictureState}
					forceUpdate={forceUpdate}
					userOffline={userOffline}
					dynamicCoverImageHeight={dynamicCoverImageHeight}
				/>
			);
		} else if (hasCoverPicture) {
			return (
				<HoverOverlayAndDropdownItems
					userOffline={userOffline}
					closeModal={closeModal}
					openMediaBrowser={openMediaBrowser}
					setIsRepositionActive={setIsRepositionActive}
					onRemoveImage={onRemoveImage}
					handleUnsplashUpload={handleUnsplashUpload}
					setIsUploadingPicture={setIsUploadingPicture}
					initialUnsplashInfoArray={initialUnsplashInfoArray}
					setInitialUnsplashInfoArray={setInitialUnsplashInfoArray}
					coverPictureState={coverPictureState}
					contentId={contentId}
					onImageWidthChange={onImageWidthChange}
					spaceKey={spaceKey}
					dynamicCoverImageHeight={dynamicCoverImageHeight}
					isFixedWidthImageOption={isFixedWidthImageOption}
					generateAIHeaderImage={generateAIHeaderImage}
				/>
			);
		} else {
			// no cover picture
			return (
				<AddHeaderImageButton
					userOffline={userOffline}
					openModal={openModal}
					closeModal={closeModal}
					openMediaBrowser={openMediaBrowser}
					isModalOpenState={isModalOpenState}
					handleUnsplashUpload={handleUnsplashUpload}
					setIsUploadingPicture={setIsUploadingPicture}
					initialUnsplashInfoArray={initialUnsplashInfoArray}
					setInitialUnsplashInfoArray={setInitialUnsplashInfoArray}
					coverPictureState={coverPictureState}
					floatingToolbarButtonStyle={floatingToolbarButtonStyle}
					contentId={contentId}
					generateAIHeaderImage={generateAIHeaderImage}
				/>
			);
		}
	},
);
